/**
 * The Deep's elements (05 §2): key, door, clock, expanding wall, slime. Pure.
 */

import { midiToHz } from '../notes';
import { vary } from '../rand';
import { adsr, type VoiceSpec } from '../voice';
import { hit, type Sfx, tone } from './kit';

/** Picking up a key: two pulse pings a fifth apart, E6 then B6. */
export const key: Sfx = (r) => {
  const f = vary(r, midiToHz(88), 0.01);
  const ping = (hz: number, at: number, decay: number): VoiceSpec => ({
    ...hit('pulse', hz, decay, 0.22),
    at,
    width: 0.35,
  });
  return [ping(f, 0, 0.18), ping(f * 1.5, 0.08, 0.3)];
};

/** A door of that colour opens: a heavy latch - "ka-CHUNK", a clunk then a thud with a clack. */
export const doorOpen: Sfx = (r) => [
  {
    ...hit('noise', vary(r, 2200, 0.1), 0.12, 0.45),
    to: 300,
    glide: 0.08,
    filter: { type: 'lowpass', freq: 1600, q: 4 },
  },
  { ...hit('triangle', vary(r, 140, 0.05), 0.2, 0.45), at: 0.07, to: 60, glide: 0.12 },
  {
    ...hit('pulse', 330, 0.07, 0.14),
    at: 0.07,
    width: 0.1,
    filter: { type: 'bandpass', freq: 1200, q: 3 },
  },
];

/**
 * Picking up a clock: a bell struck twice. Each strike is a triangle at A5 with an inharmonic
 * partial (x2.76) that fades first, so it rings like metal rather than a pure tone.
 */
export const clock: Sfx = (r) => {
  const f = vary(r, midiToHz(81), 0.005);
  const strike = (at: number, level: number): VoiceSpec[] => [
    { ...hit('triangle', f, 1.1, level), at },
    { ...hit('triangle', f * 2.76, 0.45, level * 0.4), at },
  ];
  return [...strike(0, 0.26), ...strike(0.2, 0.2)];
};

/** An expanding wall grows a brick: a short scrape of band-passed noise sweeping up. */
export const expand: Sfx = (r) => [
  {
    ...tone('noise', vary(r, 1100, 0.15), 0.05, adsr(0.004, 0.05, 0.3, 0.04), 0.2),
    filter: { type: 'bandpass', freq: 650, to: 1100, sweep: 0.08, q: 3 },
  },
];

/** Something drops through slime: a drip, a triangle "plip" gliding up. */
export const slime: Sfx = (r) => {
  const f = vary(r, 520, 0.15);
  return [{ ...hit('triangle', f, 0.09, 0.24), to: f * 2.6, glide: 0.05 }];
};
